<template>
  <div class="user-avatar-wrapper">
    <base-avatar
      :image-url="user?.avatarUrl"
      :label="name"
      :show-name="props.showName"
      :light-text="props.lightText"
      :loading="props.loading"
      :size="props.size"
      :has-border="props.hasBorder"
      :border-color="props.borderColor"
      :show-tooltip="props.showTooltip"
      :is-devstride="props.isDevstride"
      :shape="props.shape"
      :fallback-icon="props.fallbackIcon"
    />

    <!-- Inactive user warning -->
    <span
      v-if="
        props.member?.status === OrganizationMembershipStatus.INACTIVE &&
        !props.hideInactiveWarning
      "
      class="tw-ml-1 tw-flex tw-justify-center"
    >
      <fluent-icon icon="warning" class="tw-text-yellow" />
      <q-tooltip
        :delay="300"
        :offset="[3, 3]"
        anchor="top middle"
        self="bottom middle"
      >
        User is inactive
      </q-tooltip>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps, withDefaults } from 'vue';
import {
  OrganizationMembership,
  OrganizationMembershipStatus,
} from '@/models/user.models';
import { Colors } from '@/api';
import BaseAvatar from './BaseAvatar.vue';

type AvatarShape = 'circle' | 'square' | 'rounded';

interface Props {
  /** Organization membership containing user data */
  member?: OrganizationMembership;
  /** Show user name next to avatar */
  showName?: boolean;
  /** Use lighter text color */
  lightText?: boolean;
  /** Show loading spinner */
  loading?: boolean;
  /** Text to show when no user */
  emptyName?: string;
  /** Avatar size (e.g., '24px', '32px') */
  size?: string;
  /** Add border around avatar */
  hasBorder?: boolean;
  /** Border color */
  borderColor?: Colors;
  /** Show tooltip on hover */
  showTooltip?: boolean;
  /** If true, will use the devstride logo as the avatar */
  isDevstride?: boolean;
  /** If true, hides the warning icon for inactive users */
  hideInactiveWarning?: boolean;
  /** Avatar shape */
  shape?: AvatarShape;
  /** Fallback icon name (Octicon) when no avatar image */
  fallbackIcon?: string;
}

const props = withDefaults(defineProps<Props>(), {
  emptyName: 'No User',
  hasBorder: false,
  showTooltip: false,
  shape: 'circle',
  fallbackIcon: 'person',
});

const user = computed(() => {
  if (!props.member) return;
  return props.member.user;
});

const name = computed(() => {
  if (props.isDevstride) return 'DevStride';
  return props.member?.user?.username
    ? props.member.user.name || props.member.user.username
    : props.emptyName;
});
</script>

<style lang="scss" scoped>
.user-avatar-wrapper {
  @apply tw-flex tw-items-center;
}
</style>
